import { useReducer, FC } from 'react'
import { css } from '@emotion/react'
import { typography } from '@lifedot/styles/typography'
import { palette } from '@lifedot/styles/palette'
import { mq } from '@lifedot/styles/mediaQuery'
import { elevation } from '@lifedot/styles/elevation'
import { Button } from '@lifedot/atoms/Button'
import { useDiscoverDialogue } from '@lifedot/hooks/use-discover-dialogue'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { router } from '@lifedot/router'
import { dialogueAnswers, STEP } from '@lifedot/constants/dialogue'

const styles = {
  wrapper: css({
    width: '100%',
    maxWidth: 740,
    margin: '24px auto 0',
    border: `1px solid ${palette.main}`,
    borderRadius: 4,
    backgroundColor: palette.background
  }),
  headWrap: css({
    backgroundColor: palette.main,
    padding: 8,
    textAlign: 'center',
    borderRadius: '3px 3px 0 0',
    [mq('sp')]: {
      textAlign: 'left'
    }
  }),
  head: css({
    display: 'inline-block',
    position: 'relative',
    [mq('sp')]: {
      paddingLeft: '14%'
    },
    [mq('max', 320)]: {
      padding: 0
    }
  }),
  balloon: css({
    border: `1px solid ${palette.main}`,
    borderRadius: 50,
    backgroundColor: palette.white,
    padding: '4px 12px',
    position: 'absolute',
    top: -32,
    left: -60,
    color: palette.main03,
    fontWeight: 'bold',
    [mq('sp')]: {
      left: 0
    },
    '::before': {
      content: '""',
      position: 'absolute',
      bottom: -16,
      left: '12%',
      border: '6px solid transparent',
      borderTop: `10px solid ${palette.main}`
    },
    '::after': {
      content: '""',
      position: 'absolute',
      bottom: -14,
      left: '12%',
      border: '6px solid transparent',
      borderTop: `10px solid ${palette.white}`
    }
  }),
  img: css({
    position: 'absolute',
    bottom: -8,
    right: -80,
    fontSize: 0,
    [mq('sp')]: {
      right: -72
    }
  }),
  content: css({
    display: 'grid',
    gap: 24,
    padding: 32,
    [mq('sp')]: {
      padding: 16
    }
  }),
  qa: css({
    display: 'grid',
    gap: 12
  }),
  button: css({
    backgroundColor: palette.white,
    boxShadow: elevation[1],
    borderRadius: 4,
    padding: '0 20px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'relative',
    height: 50,
    '::before': {
      content: '""',
      border: `2px solid ${palette.gray2}`,
      width: 20,
      height: 20,
      borderRadius: '50%',
      marginRight: 8
    }
  }),
  selected: css({
    fontWeight: 'bold',
    '::after': {
      content: '""',
      position: 'absolute',
      left: 26,
      backgroundColor: palette.main,
      width: 12,
      height: 12,
      borderRadius: '50%',
      marginRight: 8
    }
  }),
  question: css({
    fontWeight: 'bold',
    margin: '0 !important'
  }),
  error: css({
    color: palette.alert,
    margin: '0 !important'
  })
}

export const DialogueLink: FC = () => {
  const [{ answers, status }, { answer }] = useDiscoverDialogue(STEP.FIRST)
  const [selected, select] = useReducer((...args: [boolean, string]) => {
    answer(args[1])
    return true
  }, false)
  const selectAnswer = answers[STEP.FIRST]
  const [isError, setIsError] = useReducer(() => true, false)
  const nextRouter = useRouter()
  const handleSubmit = () => {
    if (status === 'ANSWERED' && selected)
      nextRouter.push(router.dialogueStep(2))
    else setIsError()
  }

  return (
    <div css={styles.wrapper}>
      <div css={styles.headWrap}>
        <div css={styles.head}>
          <span css={typography.headingL}>ぴったりお墓診断</span>
          <p css={[typography.textS, styles.balloon]}>4つの質問で見つかる！</p>
          <div css={styles.img}>
            <Image
              src="https://s3.lifedot.jp/uploads/ending/path/67887/icon_dialogue_link.png"
              width={68}
              height={68}
              alt=""
            />
          </div>
        </div>
      </div>
      <div css={styles.content}>
        <div css={styles.qa}>
          <p css={[typography.textL, styles.question]}>
            Q. お墓は代々継いでいきたいですか？
          </p>
          <button
            onClick={() => select(dialogueAnswers.sectionType.yes)}
            css={[
              styles.button,
              selectAnswer === dialogueAnswers.sectionType.yes &&
                styles.selected
            ]}
          >
            はい
          </button>
          <button
            onClick={() => select(dialogueAnswers.sectionType.no)}
            css={[
              styles.button,
              selectAnswer === dialogueAnswers.sectionType.no && styles.selected
            ]}
          >
            いいえ
          </button>
          <button
            onClick={() => select(dialogueAnswers.sectionType.unknown)}
            css={[
              styles.button,
              selectAnswer === dialogueAnswers.sectionType.unknown &&
                styles.selected
            ]}
          >
            わからない
          </button>
          {isError && !selected && (
            <p css={[typography.textS, styles.error]}>
              選択肢をどれか一つ選んでください
            </p>
          )}
        </div>
        <Button
          mainText={<span css={typography.textXL}>診断スタート</span>}
          onClick={handleSubmit}
          color="accent"
        />
      </div>
    </div>
  )
}
